// Every line the sign-in screens show, from the spec (C1, C7), the board and docs/plans/sign-in.md.
// Lines marked "proposed" are not in the spec or on the board; see the PR for #32.
import type { Failure } from './api';

export const copy = {
  footnote: 'Accounts are created by your manager.',
  signIn: 'Sign in',
  signingIn: 'Signing in...',
  forgot: 'Forgot password?',
  wrongPassword: 'Email or password is incorrect.',
  locked: 'Too many attempts. Try again in 15 minutes.',
  disabled: 'This account is disabled. Contact your manager.',
  expired: 'You were signed out after 12 hours. Sign in again to continue.',
  goBack: 'You go back to the page you were on.',
  signedOut: 'You signed out.',

  checkEmail: 'Check your email',
  sentTo: (email: string) => `We sent a 6-digit code to ${email}`,
  verify: 'Verify',
  remember: 'Remember this device for 30 days',
  resent: 'We sent a new code. Use the newest email.',
  wrongCode: 'That code is not right. Check the newest email from Safeside and try again.',
  codeExpired: 'That code has expired. Send a new code.',
  tooManyTries: 'Too many wrong tries. Send a new code.',
  help: 'Having issues signing in? Contact your manager.',

  setYourPassword: 'Set your password',
  forEmail: (email: string) => `For ${email}`,
  newPassword: 'New password',
  confirmPassword: 'Confirm password',
  setAndSignIn: 'Set password and sign in',
  // proposed
  checkingLink: 'Checking your link...',
  // proposed
  linkProblem: 'This link has expired or was already used.',
  sendNewLink: 'Send a new link',
  backToSignIn: 'Back to sign in',

  resetTitle: 'Reset your password',
  resetIntro: 'We will email you a link to set a new one.',
  sendResetLink: 'Send reset link',
  // proposed
  sendingResetLink: 'Sending...',
  resetSent: 'If this email has an account, a reset link is on its way.',

  myProfile: 'My profile',
  changePassword: 'Change password',
  signOut: 'Sign out',
  managedByManager: 'Managed by your Manager in Settings, Users.',
  managedByAdmin: 'Managed by the Admin in Settings, Users.',
  notifications: 'Notifications',
  phase2: 'Phase 2',
  twoStep: 'Two-step sign-in',
  codeByEmail: 'Code by email',
  on: 'On',
  rememberedUntil: (date: string) => `This device is remembered until ${date}`,
  notRemembered: 'This device is not remembered for you. Each sign-in asks for a code.',
  forgetDevice: 'Forget this device',
  deviceForgotten: (name: string) =>
    `This device is forgotten for ${name}. Your next sign-in asks for a code.`,
  close: 'Close',
  cancel: 'Cancel',
  currentPassword: 'Current password',
  confirmNewPassword: 'Confirm new password',
  passwordChanged: 'Password changed. Other devices were signed out.',
  // proposed
  wrongCurrent: 'Current password is not right.',

  // proposed
  tryAgainIn: (minutes: number) =>
    `Too many attempts. Try again in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`,
  // proposed
  timedOut: 'Your sign-in timed out. Sign in again to get a new code.',
  // proposed
  tooManyCodes: 'Too many codes sent. Sign in again to get a new one.',
  // proposed
  somethingWrong: 'Something went wrong. Try again.',
} as const;

/** Notices the code screen hands back to Sign in. */
export type SignInNotice = 'disabled' | 'timed_out' | 'too_many_codes';

export const noticeText: Record<Exclude<SignInNotice, 'disabled'>, string> = {
  timed_out: copy.timedOut,
  too_many_codes: copy.tooManyCodes,
};

/** The per-IP limit answers with the seconds left; shown in whole minutes. */
export function rateLimitText(f: Failure): string {
  const seconds = typeof f.body.retryAfter === 'number' ? f.body.retryAfter : 15 * 60;
  return copy.tryAgainIn(Math.max(1, Math.ceil(seconds / 60)));
}

/** What Sign in shows for a failed password step. */
export function signInFailure(f: Failure): { disabled: true } | { disabled: false; text: string } {
  if (f.error === 'disabled') return { disabled: true };
  if (f.error === 'invalid') return { disabled: false, text: copy.wrongPassword };
  if (f.error === 'locked') return { disabled: false, text: copy.locked };
  if (f.error === 'rate_limited') return { disabled: false, text: rateLimitText(f) };
  return { disabled: false, text: copy.somethingWrong };
}
